import { sanitizeObject, ternaryHandler, getPageData, getUserData } from '@marriott/mi-headless-utils';

type SessionShape = {
  data?: {
    AriesReservation?: {
      availabilityRequestVO?: {
        rewardsRedemption?: unknown;
      };
    };
  };
};

/**
 * Returns getPageData() output with query_string and hash omitted.
 * Book events (ROOM_LIST, CHECKOUT, PURCHASE) do not include URL-derived keys in their page payload.
 */
const getPageDataWithoutUrlKeys = () => {
  const rawPageData = getPageData({ isModel: false, modelName: '' }) as Record<string, unknown>;
  const filteredPageData = { ...rawPageData };
  if (filteredPageData['query_string']) {
    delete filteredPageData['query_string'];
  }
  if (filteredPageData['hash']) {
    delete filteredPageData['hash'];
  }
  return filteredPageData;
};

/**
 * Returns the member rate type from the datalayer.
 * @param datalayer - The current datalayer object from useSession()
 * @returns The member rate type from the datalayer. "Member" or "Non-Member" or "".
 */
const getMemberRateType = (datalayer: Record<string, unknown>) => {
  // res_rate_is_member_rate is a boolean (or boolean string) in the datalayer; map to the expected string values.
  return datalayer?.['res_rate_is_member_rate'] != null
    ? String(datalayer?.['res_rate_is_member_rate']) === 'true'
      ? 'Member'
      : 'Non-Member'
    : '';
};

/**
 * Shared datalayer-sourced fields common to every ROOM product shape.
 * Internal helper — not exported. Each caller adds its own identity fields
 * (category, subcategory, id, property_id) and any event-specific extras.
 */
const buildRoomProductBase = (datalayer: Record<string, unknown>) => ({
  brand: ternaryHandler(datalayer?.['prop_brand_name']),
  check_in_date: ternaryHandler(datalayer?.['search_date_check_in']),
  check_out_date: ternaryHandler(datalayer?.['search_date_check_out']),
  cluster_code: ternaryHandler(datalayer?.['search_cluster_code']),
  number_of_adults: ternaryHandler(datalayer?.['res_num_adults']),
  number_of_children: ternaryHandler(datalayer?.['res_num_children']),
  property_tier: ternaryHandler(datalayer?.['prop_brand_tier']),
  property_code: ternaryHandler(datalayer?.['prop_brand_code']),
  stay_day_types: ternaryHandler(datalayer?.['search_is_weekend_stay']),
  stay_day_of_week: ternaryHandler(datalayer?.['search_date_check_in_day_of_week']),
});

/**
 * Generates the ACDL payload for the ROOM_LIST event.
 * Fired on initial page load of /reservation/rateListMenu.mi (Rate List Menu page).
 *
 * Usage:
 *   updateDatalayer({ event: 'ROOM_LIST', ...getRoomListEventData(datalayer) })
 *
 * @param datalayer - The current datalayer object from useSession()
 * @returns Sanitized payload object containing page, user, products, and reservation keys.
 */
export const getRoomListEventData = (datalayer: Record<string, unknown>, session: SessionShape) => {
  const rewardRedemptionFromSession =
    session?.data?.AriesReservation?.availabilityRequestVO?.rewardsRedemption || false;
  return sanitizeObject({
    page: getPageDataWithoutUrlKeys(),
    user: getUserData(datalayer),
    products: [
      sanitizeObject({
        category: 'ROOM',
        subcategory: 'ROOM',
        id: ternaryHandler(datalayer?.['prop_marsha_code']),
        property_id: ternaryHandler(datalayer?.['prop_marsha_code']),
        ...buildRoomProductBase(datalayer),
      }),
    ],
    reservation: sanitizeObject({
      advance_purchase_days: ternaryHandler(datalayer?.['search_advance_purchase_days']),
      booking_engine: ternaryHandler(datalayer?.['crsSystem']),
      check_in_date: ternaryHandler(datalayer?.['search_date_check_in']),
      check_out_date: ternaryHandler(datalayer?.['search_date_check_out']),
      number_of_rooms: ternaryHandler(datalayer?.['res_rooms_per_night']),
      display_points_value: datalayer?.['isRewardRedemption']
        ? datalayer?.['isRewardRedemption'] === 'true'
        : rewardRedemptionFromSession,
    }),
  });
};

/**
 * Generates the ACDL payload for the CHECKOUT event.
 * Fired on initial page load of:
 *   - /reservation/reviewguestinformation.mi (Review Guest Info — RGI)
 *   - /reservation/reviewDetails.mi (Review Reservation Details — RRD)
 *
 * Products are mapped from datalayer.products[]:
 *   - ROOM items carry room-level and reservation-level fields.
 *   - NRP (Non-Room Product) items carry only product-level fields.
 *
 * Usage:
 *   updateDatalayer({ event: 'CHECKOUT', ...getCheckoutEventData(datalayer) })
 *
 * @param datalayer - The current datalayer object from useSession()
 * @returns Sanitized payload object containing page, user, products, reservation, and carts keys.
 */
export const getCheckoutEventData = (datalayer: Record<string, unknown>) => {
  const dlProducts = (datalayer?.['products'] as Array<Record<string, unknown>>) ?? [];

  const products = dlProducts.map(product => {
    let prdObj: Record<string, unknown> = {
      category: ternaryHandler(product['productCategory']),
      subcategory: ternaryHandler(product['productSubCategory']),
      id: ternaryHandler(product['productID']),
      name: ternaryHandler(product['productName']),
      property_id: ternaryHandler(product['propertyID']),
      quantity: ternaryHandler(product['quantity']),
      cost: ternaryHandler(product['cost']),
    };
    if (product['productCategory'] === 'ROOM') {
      prdObj = {
        ...prdObj,
        ...buildRoomProductBase(datalayer),
        number_of_points_redeemed: ternaryHandler(datalayer?.['res_total_points_for_stay']),
        rate_market_prefix: ternaryHandler(datalayer?.['res_market_prefix']),
        rate_program_code: ternaryHandler(datalayer?.['res_rate_program']),
        room_pool_code: ternaryHandler(product['roomPoolCode']),
        member_rate_type: getMemberRateType(datalayer),
      };
    }
    return sanitizeObject(prdObj) as Record<string, unknown>;
  });

  return sanitizeObject({
    page: getPageDataWithoutUrlKeys(),
    user: getUserData(datalayer),
    products,
    reservation: sanitizeObject({
      advance_purchase_days: ternaryHandler(datalayer?.['search_advance_purchase_days']),
      check_in_date: ternaryHandler(datalayer?.['search_date_check_in']),
      check_out_date: ternaryHandler(datalayer?.['search_date_check_out']),
      booking_engine: ternaryHandler(datalayer?.['crsSystem']),
      number_of_rooms: ternaryHandler(datalayer?.['res_rooms_per_night']),
    }),
    carts: [
      sanitizeObject({
        cart_id: ternaryHandler((datalayer?.['cart'] as Array<Record<string, unknown>>)?.[0]?.['cartID']),
      }),
    ],
  });
};

/**
 * Generates the ACDL payload for the PURCHASE event.
 * Fired once after PAGEVIEW on the Confirmation page (/reservation/confirmation.mi).
 *
 * Products are mapped from datalayer.products[]:
 *   - ROOM items carry all shared room fields (via buildRoomProductBase) plus three
 *     PURCHASE-only extras: confirmation_id, nights_from_points, nights_from_cash.
 *   - NRP items carry only the base product fields (category, subcategory, id, name,
 *     property_id, quantity, cost).
 *
 * Usage:
 *   updateDatalayer({ event: 'PURCHASE', ...getPurchaseEventData(datalayer) })
 *
 * @param datalayer - The current datalayer object from useSession()
 * @returns Sanitized payload containing page, user, products, carts, and transaction keys.
 */
export const getPurchaseEventData = (datalayer: Record<string, unknown>) => {
  const dlProducts = (datalayer?.['products'] as Array<Record<string, unknown>>) ?? [];
  const dlTransaction = (datalayer?.['transaction'] as Record<string, unknown>) ?? {};

  // Sum quantity of all non-ROOM products for transaction.total_number_of_non_room_products.
  const nonRoomProductCount = dlProducts
    .filter(p => p['productCategory'] !== 'ROOM')
    .reduce((sum, p) => sum + (Number(p['quantity']) || 0), 0);

  const products = dlProducts.map(product => {
    let prdObj: Record<string, unknown> = {
      category: ternaryHandler(product['productCategory']),
      subcategory: ternaryHandler(product['productSubCategory']),
      id: ternaryHandler(product['productID']),
      name: ternaryHandler(product['productName']),
      property_id: ternaryHandler(product['propertyID']),
      quantity: ternaryHandler(product['quantity']),
      cost: ternaryHandler(product['cost']),
    };
    if (product['productCategory'] === 'ROOM') {
      prdObj = {
        ...prdObj,
        ...buildRoomProductBase(datalayer),
        confirmation_id: ternaryHandler(datalayer?.['res_confirmation_number']),
        nights_from_points: ternaryHandler(product['res_points_nights']),
        nights_from_cash: ternaryHandler(product['res_cash_nights']),
        rate_market_prefix: ternaryHandler(datalayer?.['res_market_prefix']),
        rate_program_code: ternaryHandler(datalayer?.['res_rate_program']),
        room_pool_code: ternaryHandler(product['roomPoolCode']),
        member_rate_type: getMemberRateType(datalayer),
      };
    }
    return sanitizeObject(prdObj) as Record<string, unknown>;
  });

  return sanitizeObject({
    page: getPageDataWithoutUrlKeys(),
    user: getUserData(datalayer),
    products,
    carts: [
      sanitizeObject({
        cart_id: ternaryHandler((datalayer?.['cart'] as Array<Record<string, unknown>>)?.[0]?.['cartID']),
      }),
    ],
    transaction: sanitizeObject({
      booking_engine: ternaryHandler(datalayer?.['crsSystem']),
      check_in_date: ternaryHandler(datalayer?.['search_date_check_in']),
      check_out_date: ternaryHandler(datalayer?.['search_date_check_out']),
      parent_order_id: ternaryHandler(datalayer?.['res_confirmation_number']),
      total_number_of_rooms: ternaryHandler(datalayer?.['res_rooms_per_night']),
      total_number_of_room_nights: ternaryHandler(datalayer?.['res_room_nights_total']),
      total_number_of_non_room_products: nonRoomProductCount,
      total_usd_subtotal: ternaryHandler(dlTransaction['totalAmount']),
      total_usd_fees: ternaryHandler(dlTransaction['totalFees']),
      total_usd_taxes: ternaryHandler(dlTransaction['totalTaxes']),
      payments: buildPaymentsArray(datalayer, dlTransaction),
    }),
  });
};

/**
 * Builds the payments array for the PURCHASE event transaction object.
 * Splits res_cash_points_mix on "+" (e.g. "Cash+Points" → ["Cash", "Points"]) and
 * maps each token to a payment entry using the datalayer fields documented below.
 *
 * Supported types and their field mappings:
 *  - "Credit Card" : currency_code = transaction.currencyCode, credit_card_type = res_payment_type, amount = ""
 *  - "Points"      : currency_code = "", amount = res_total_points_for_stay
 *  - "Gift Card"   : currency_code = "", amount = ""
 *  - "Certificate" : currency_code = null (hardcoded), amount = res_FNA_nights
 *
 * usd_value (all types except Certificate) = SUM(datalayer.products[].cost)
 */
const buildPaymentsArray = (
  datalayer: Record<string, unknown>,
  dlTransaction: Record<string, unknown>
): Array<Record<string, unknown>> => {
  const rawMix = datalayer?.['res_cash_points_mix'];
  if (!rawMix || typeof rawMix !== 'string') return [];

  const usdValue = ((datalayer?.['products'] as Array<Record<string, unknown>>) ?? []).reduce(
    (sum, p) => sum + (Number(p['cost']) || 0),
    0
  );

  const tokens = rawMix.split('+').map(t => t.trim());

  // @TODO: Few keys are set as "" since mapping is not provdided and those will be part of V2
  return tokens
    .map(token => {
      switch (token) {
        case 'Cash':
          return sanitizeObject({
            currency_code: ternaryHandler(dlTransaction['currencyCode']),
            type: 'Cash',
            credit_card_type: ternaryHandler(datalayer?.['res_payment_type']),
            amount: '',
            usd_value: ternaryHandler(usdValue),
          }) as Record<string, unknown>;

        case 'Points':
          return sanitizeObject({
            currency_code: '',
            type: 'Points',
            amount: ternaryHandler(datalayer?.['res_total_points_for_stay']),
            usd_value: ternaryHandler(usdValue),
          }) as Record<string, unknown>;

        case 'Gift Card':
          return sanitizeObject({
            currency_code: '',
            type: 'Gift Card',
            amount: '',
            usd_value: ternaryHandler(usdValue),
          }) as Record<string, unknown>;

        case 'Certificate':
          return sanitizeObject({
            currency_code: null,
            type: 'Certificate',
            amount: ternaryHandler(datalayer?.['res_FNA_nights']),
          }) as Record<string, unknown>;

        default:
          return null;
      }
    })
    .filter((entry): entry is Record<string, unknown> => entry !== null);
};
